import type * as React from "react";

import { CalendarIcon } from "@lucide/icons";
import { isValid } from "date-fns";

import { type CalendarDate, createDate, fromParts } from "../dateAdapter";
import { tick } from "../lib";
import { invoke, isNil } from "../lib/utils";
import type { BasePickerOnChangeData } from "../pickers/BasePicker";
import YearPicker from "../pickers/YearPicker";
import DayPicker from "../pickers/dayPicker/DayPicker";
import MonthPicker from "../pickers/monthPicker/MonthPicker";
import HourPicker from "../pickers/timePicker/HourPicker";
import MinutePicker from "../pickers/timePicker/MinutePicker";
import InputView from "../views/InputView";
import BaseInput, {
  type BaseInputProps,
  type BaseInputState,
  type DateRelatedProps,
  type DisableValuesProps,
  type MarkedValuesProps,
  type MinMaxValueProps,
  type MultimodeProps,
  type TimeRelatedProps,
} from "./BaseInput";
import {
  TIME_FORMAT,
  buildValue,
  dateValueToString,
  parseArrayOrValue,
  parseValue,
} from "./parse";
import {
  clampMinMaxToValue,
  getDisabledMonths,
  getDisabledYears,
} from "./shared";

type CalendarMode = "year" | "month" | "day" | "hour" | "minute";

const nextMode: { [key: string]: CalendarMode } = {
  year: "month",
  month: "day",
  day: "hour",
  hour: "minute",
  minute: "year",
};

function getNextMode(currentMode: CalendarMode): CalendarMode {
  return nextMode[currentMode]!;
}

const prevMode: { [key: string]: CalendarMode } = {
  minute: "hour",
  hour: "day",
  day: "month",
  month: "year",
  year: "minute",
};

function getPrevMode(currentMode: CalendarMode): CalendarMode {
  return prevMode[currentMode]!;
}

export interface DateTimeInputProps
  extends
    BaseInputProps,
    DateRelatedProps,
    TimeRelatedProps,
    MultimodeProps,
    DisableValuesProps,
    MarkedValuesProps,
    MinMaxValueProps {
  startMode?: "year" | "month" | "day";
  /** Date and time divider. */
  divider?: string;
  /** Preserve last mode (day, hour, minute) each time user opens dialog. */
  preserveViewMode?: boolean;
  /** Datetime formatting string. */
  dateTimeFormat?: string;
  /** If true, minutes picker won't be shown after picking the hour. Default: false */
  disableMinute?: boolean;
}

export type DateTimeInputOnChangeData = DateTimeInputProps;

interface DateTimeInputState extends BaseInputState {
  mode: CalendarMode;
  year: number | undefined;
  month: number | undefined;
  date: number | undefined;
  hour: number | undefined;
  minute: number | undefined;
}

class DateTimeInput extends BaseInput<DateTimeInputProps, DateTimeInputState> {
  /**
   * Component responsibility:
   *  - parse input value
   *  - handle underlying picker change
   */
  public static readonly defaultProps = {
    ...BaseInput.defaultProps,
    dateFormat: "dd-MM-yyyy",
    timeFormat: "24",
    startMode: "day",
    divider: " ",
    icon: CalendarIcon,
    preserveViewMode: true,
    disableMinute: false,
  };

  constructor(props: DateTimeInputProps) {
    super(props);
    const parsedValue = parseValue(
      props.value,
      this.getDateTimeFormat(),
      props.localization,
    );
    this.state = {
      mode: props.startMode || "day",
      year: parsedValue ? parsedValue.year() : undefined,
      month: parsedValue ? parsedValue.month() : undefined,
      date: parsedValue ? parsedValue.date() : undefined,
      hour: parsedValue ? parsedValue.hour() : undefined,
      minute: parsedValue ? parsedValue.minute() : undefined,
      popupIsClosed: true,
    };
  }

  public componentDidUpdate = (prevProps: DateTimeInputProps) => {
    // update internal date if ``value`` prop changed and successuffly parsed
    if (prevProps.value !== this.props.value) {
      const parsed = parseValue(
        this.props.value,
        this.getDateTimeFormat(),
        this.props.localization,
      );
      if (parsed) {
        this.setState({
          year: parsed.year(),
          month: parsed.month(),
          date: parsed.date(),
          hour: parsed.hour(),
          minute: parsed.minute(),
        });
      }
    }
  };

  public render() {
    const {
      value,
      dateTimeFormat,
      dateFormat,
      timeFormat,
      initialDate,
      disable,
      maxDate,
      minDate,
      preserveViewMode,
      startMode,
      divider,
      closable,
      markColor,
      marked,
      localization,
      onChange,
      disableMinute,
      ...rest
    } = this.props;

    return (
      <InputView
        popupIsClosed={this.state.popupIsClosed}
        closePopup={this.closePopup}
        openPopup={this.openPopup}
        onFocus={this.onFocus}
        onMount={this.onInputViewMount}
        onChange={this.onInputValueChange}
        {...(rest as any)}
        value={dateValueToString(value, this.getDateTimeFormat(), localization)}
        renderPicker={() => this.getPicker()}
      />
    );
  }

  private parseInternalValue(): CalendarDate | undefined {
    /*
      Creates moment instance from values stored in component's state
      (year, month, date, hour, minute) in order to pass this moment instance to
      underlying picker.
      Return undefined if none of these state fields has value.
    */
    const { year, month, date, hour, minute } = this.state;
    if (
      !isNil(year) ||
      !isNil(month) ||
      !isNil(date) ||
      !isNil(hour) ||
      !isNil(minute)
    ) {
      return fromParts({ year, month, date, hour, minute });
    }
  }

  private getDateTimeFormat(): string {
    const { dateFormat, divider, timeFormat, dateTimeFormat } = this.props;

    return (
      dateTimeFormat || `${dateFormat}${divider}${TIME_FORMAT[timeFormat!]}`
    );
  }

  private getPicker(): React.ReactNode {
    const {
      value,
      initialDate,
      disable,
      inline,
      marked,
      markColor,
      localization,
      tabIndex,
      pickerStyle,
      pickerWidth,
    } = this.props;
    const { minDate, maxDate } = clampMinMaxToValue(
      value,
      this.props.minDate,
      this.props.maxDate,
    );
    const dateTimeFormat = this.getDateTimeFormat();
    const pickerProps = {
      tabIndex,
      isPickerInFocus: this.isPickerInFocus,
      isTriggerInFocus: this.isTriggerInFocus,
      inline,
      pickerWidth,
      pickerStyle,
      onCalendarViewMount: this.onCalendarViewMount,
      closePopup: this.closePopup,
      onChange: this.handleSelect,
      onHeaderClick: this.switchToPrevMode,
      initializeWith: buildValue(
        this.parseInternalValue(),
        initialDate,
        localization,
        dateTimeFormat,
      ),
      value: buildValue(value, null, localization, dateTimeFormat, null),
      minDate: parseValue(minDate, dateTimeFormat, localization),
      maxDate: parseValue(maxDate, dateTimeFormat, localization),
      localization,
    };
    const disableParsed = parseArrayOrValue(
      disable,
      dateTimeFormat,
      localization,
    );
    const { mode } = this.state;
    const markedParsed = parseArrayOrValue(
      marked,
      dateTimeFormat,
      localization,
    );
    if (mode === "year") {
      return (
        <YearPicker
          {...(pickerProps as any)}
          disable={getDisabledYears(disableParsed!)}
        />
      );
    }
    if (mode === "month") {
      return (
        <MonthPicker
          {...(pickerProps as any)}
          hasHeader
          disable={getDisabledMonths(disableParsed!)}
        />
      );
    }
    if (mode === "day") {
      return (
        <DayPicker
          {...(pickerProps as any)}
          marked={markedParsed}
          markColor={markColor}
          disable={disableParsed}
        />
      );
    }
    if (mode === "hour") {
      return (
        <HourPicker
          timeFormat={this.props.timeFormat}
          hasHeader
          {...(pickerProps as any)}
          disable={disableParsed}
        />
      );
    }

    return (
      <MinutePicker
        timeFormat={this.props.timeFormat}
        hasHeader
        {...(pickerProps as any)}
        disable={disableParsed}
      />
    );
  }

  private switchToNextModeUndelayed = (): void => {
    this.setState(({ mode }) => {
      return { mode: getNextMode(mode) };
    }, this.onModeSwitch);
  };

  private switchToNextMode = (): void => {
    tick(this.switchToNextModeUndelayed);
  };

  private switchToPrevModeUndelayed = (): void => {
    this.setState(({ mode }) => {
      return { mode: getPrevMode(mode) };
    }, this.onModeSwitch);
  };

  private switchToPrevMode = (): void => {
    tick(this.switchToPrevModeUndelayed);
  };

  private handleSelect = (
    e: React.SyntheticEvent<HTMLElement> | null,
    { value }: BasePickerOnChangeData,
  ): void => {
    tick(this.handleSelectUndelayed, e, { value });
  };

  private onFocus = (): void => {
    if (!this.props.preserveViewMode) {
      this.setState({ mode: this.props.startMode! });
    }
  };

  private handleSelectUndelayed = (
    e: React.SyntheticEvent<HTMLElement> | null,
    { value }: BasePickerOnChangeData,
  ): void => {
    const { closable, disableMinute } = this.props;
    const normalizedValue =
      disableMinute && this.state.mode === "hour"
        ? { ...value, minute: 0 }
        : value;

    const closeCondA = closable && this.state.mode === "minute";
    const closeCondB = closable && disableMinute && this.state.mode === "hour";
    if (closeCondA || closeCondB) {
      this.closePopup();
    }

    const endAtMode = disableMinute ? "hour" : "minute";

    this.setState(
      (prevState) => {
        const { mode } = prevState;

        if (mode === endAtMode) {
          const outValue = fromParts(normalizedValue).format(
            this.getDateTimeFormat(),
          );
          invoke(this.props, "onChange", e, { ...this.props, value: outValue });
        }

        return {
          year: normalizedValue.year,
          month: normalizedValue.month,
          date: normalizedValue.date,
          hour: normalizedValue.hour,
          minute: normalizedValue.minute,
        };
      },
      () => this.state.mode !== endAtMode && this.switchToNextMode(),
    );
  };

  /** Keeps internal state in sync with input field value. */
  private onInputValueChange = (
    e: React.SyntheticEvent<HTMLElement>,
    { value }: { value: string },
  ) => {
    const parsedValue = createDate(value, this.getDateTimeFormat());
    if (parsedValue && isValid(parsedValue.toDate())) {
      this.setState({
        year: parsedValue.year(),
        month: parsedValue.month(),
        date: parsedValue.date(),
        hour: parsedValue.hour(),
        minute: parsedValue.minute(),
      });
    }
    invoke(this.props, "onChange", e, { ...this.props, value });
  };
}

export default DateTimeInput;
